import { inject, injectable } from 'inversify'
import * as THREE from 'three'
import type { CadDetectWithHitOptions } from '@/plugins/cad/module/api'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity/BaseEntity'
import { getPixelSizeInWorld } from '@/plugins/cad/module/common/utils'
import {
  getActiveView,
  getActiveViewSize,
  screenToActiveViewNDC,
} from '@/plugins/cad/module/common/view'
import { TYPES } from '@/plugins/cad/module/container/types'
import {
  Detector,
  type DetectorOptions,
  type MousePosition,
} from '@/plugins/cad/module/core/selector/Detector'
import type { EngineContext } from '@/runtime/EngineContext'

/**
 * CAD 实体检测器，基于射线投射查找鼠标下的实体并管理预选高亮。
 */
@injectable()
export class EntityDetector extends Detector<IEntity> {
  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EngineContext)
  private ctx!: EngineContext

  private raycaster: THREE.Raycaster = new THREE.Raycaster()
  private preSelectEntity: Set<IEntity> = new Set()
  private readonly detectThreshold: number = 10 // 默认检测阈值（像素）

  private isCadRenderable(object: THREE.Object3D): boolean {
    const candidate = object as THREE.Object3D & {
      isLine2?: boolean
      isLineSegments2?: boolean
    }
    return (
      object instanceof THREE.Line ||
      object instanceof THREE.LineSegments ||
      object instanceof THREE.Mesh ||
      candidate.isLine2 === true ||
      candidate.isLineSegments2 === true
    )
  }

  override config(option: Partial<DetectorOptions>) {
    if (option.enableHighlight === false) {
      this.clearPreselect()
    }
    super.config(option)
  }

  /**
   * 检测鼠标位置下的entity
   * @param mousePosition 鼠标屏幕坐标 {x, y}
   * @returns 检测到的entity数组
   */
  detect(mousePosition: MousePosition): IEntity[] {
    const camera = this.ctx.getService('ViewportCamera').getActiveCamera()
    const activeView = getActiveView(this.ctx)

    // 将鼠标坐标转换为标准化设备坐标 (-1 to +1)
    const mouse = screenToActiveViewNDC(this.ctx, mousePosition)
    const viewportElement = getActiveViewSize(this.ctx)

    // 使用射线投射器
    this.raycaster.setFromCamera(mouse, camera)
    this.applyRaycasterLayerMask(activeView?.layerMask)

    // 设置射线检测的阈值(根据缩放级别调整)
    const threshold = getPixelSizeInWorld(camera, viewportElement, this.detectThreshold)
    const line2Threshold = this.detectThreshold * 0.5
    this.raycaster.params.Line!.threshold = threshold
    if (!this.raycaster.params.Line2) {
      this.raycaster.params.Line2 = {
        threshold: line2Threshold,
      }
    } else {
      this.raycaster.params.Line2!.threshold = line2Threshold
    }

    // 获取场景中的所有CAD对象
    const objectGroup = this.viewManager.getObjectGroup()
    if (!objectGroup) {
      return []
    }

    // 递归获取所有子对象
    const intersectableObjects: THREE.Object3D[] = []
    objectGroup.traverse((child) => {
      // 只检测Line、LineSegments等可见对象
      if (this.isCadRenderable(child)) {
        const entity = child.userData.entity as IEntity | undefined
        const isStaticBatch = child.userData.cadBatch === true
        if (!entity && !isStaticBatch) {
          return
        }
        const isLayerVisible = entity ? this.isEntityLayerVisible(entity) : true
        if (this.isObjectVisible(child) && isLayerVisible) {
          intersectableObjects.push(child)
        }
      }
    })

    // 执行射线检测
    const intersects = this.raycaster.intersectObjects(intersectableObjects, false)

    return this.extractEntities(intersects)
  }

  /**
   * 在 NDC 坐标下执行射线投射，返回交点信息。
   *
   * @param ndc - 归一化设备坐标，x/y 均在 [-1, 1]
   * @param options - 可选相机、视口、layer 掩码
   * @returns 按距离升序的交点信息列表；无命中时返回空数组
   */
  detectWithHit(
    ndc: { x: number; y: number },
    options?: CadDetectWithHitOptions
  ): Array<{ id: string; point: THREE.Vector3; distance: number }> {
    const camera = options?.camera ?? this.ctx.getService('ViewportCamera').getActiveCamera()
    const viewport = options?.viewport
      ? {
          clientWidth: Math.max(1, Math.round(options.viewport.width)),
          clientHeight: Math.max(1, Math.round(options.viewport.height)),
        }
      : getActiveViewSize(this.ctx)

    this.raycaster.setFromCamera(new THREE.Vector2(ndc.x, ndc.y), camera)
    this.applyRaycasterLayerMask(options?.layerMask)

    const threshold = getPixelSizeInWorld(camera, viewport, this.detectThreshold)
    const line2Threshold = this.detectThreshold * 0.5
    this.raycaster.params.Line!.threshold = threshold
    if (!this.raycaster.params.Line2) {
      this.raycaster.params.Line2 = { threshold: line2Threshold }
    } else {
      this.raycaster.params.Line2!.threshold = line2Threshold
    }

    const objectGroup = this.viewManager.getObjectGroup()
    if (!objectGroup) return []

    const intersectableObjects: THREE.Object3D[] = []
    objectGroup.traverse((child) => {
      if (this.isCadRenderable(child)) {
        if ((child.userData.entity || child.userData.cadBatch) && this.isObjectVisible(child)) {
          intersectableObjects.push(child)
        }
      }
    })

    const intersects = this.raycaster.intersectObjects(intersectableObjects, false)
    const results: Array<{ id: string; point: THREE.Vector3; distance: number }> = []
    const entityIdSet = new Set<string>()

    for (const intersect of intersects.sort((a, b) => a.distance - b.distance)) {
      const entity = this.resolveIntersectedEntity(intersect.object, intersect.faceIndex)
      if (entity && this.isEntityLayerVisible(entity) && !entityIdSet.has(entity.id)) {
        results.push({ id: entity.id, point: intersect.point, distance: intersect.distance })
        entityIdSet.add(entity.id)
      }
    }

    return results
  }

  private applyRaycasterLayerMask(layerMask: number | undefined) {
    if (layerMask === undefined) {
      this.raycaster.layers.enableAll()
      return
    }

    this.raycaster.layers.mask = layerMask
  }

  /** 从相交结果中提取不重复的 entity 列表 */
  private extractEntities(intersects: THREE.Intersection[]): IEntity[] {
    const detectedEntities: IEntity[] = []
    const entityIdSet = new Set<string>()

    for (const intersect of intersects.sort((a, b) => a.distance - b.distance)) {
      const obj = intersect.object
      let entity: IEntity | undefined
      entity = this.resolveIntersectedEntity(obj, intersect.faceIndex)

      if (entity && this.isEntityLayerVisible(entity) && !entityIdSet.has(entity.id)) {
        detectedEntities.push(entity)
        entityIdSet.add(entity.id)
      }
    }

    return detectedEntities
  }

  private resolveIntersectedEntity(
    object: THREE.Object3D,
    faceIndex: number | null | undefined
  ): IEntity | undefined {
    const directEntity = object.userData?.entity as IEntity | undefined
    if (directEntity) return directEntity
    if (object.userData?.cadBatch !== true || faceIndex == null) return undefined

    const ranges = object.userData.cadBatchEntityRanges as
      | Array<{ entityId: string; segmentStart: number; segmentCount: number }>
      | undefined
    if (!ranges) return undefined
    const range = ranges.find(
      (candidate) =>
        faceIndex >= candidate.segmentStart &&
        faceIndex < candidate.segmentStart + candidate.segmentCount
    )
    return range ? this.viewManager.getEntity(range.entityId) : undefined
  }

  private isEntityLayerVisible(entity: IEntity): boolean {
    return this.viewManager.dataManager.getLayer(entity.layerId)?.visible ?? true
  }

  /**
   * 处理鼠标移动事件：检测鼠标下的实体并驱动预选高亮状态。
   *
   * @param mousePosition - 鼠标屏幕坐标；缺省时认为离开绘图区
   * @returns 当前检测到的实体列表
   */
  handleDetect(mousePosition: MousePosition | undefined): IEntity[] {
    const entities = super.handleDetect(mousePosition)
    this.handlePreselect(entities.slice(0, 1))

    return entities
  }

  private handlePreselect(entities: IEntity[]) {
    // entity预选状态显示
    if (!this.getEnableHighlight()) {
      this.clearPreselect()
      return
    }
    this.preSelectEntity.forEach((entity) => {
      if (!entities.includes(entity)) {
        this.viewManager.cancelHoverEntity(entity)
      }
    })
    entities.forEach((entity) => {
      if (!this.preSelectEntity.has(entity)) {
        this.viewManager.hoverEntity(entity)
      }
    })
    this.preSelectEntity = new Set(entities)
  }

  private clearPreselect() {
    this.preSelectEntity.forEach((entity) => {
      this.viewManager.cancelHoverEntity(entity)
    })
    this.preSelectEntity.clear()
  }

  private isObjectVisible(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (!current.visible) {
        return false
      }
      current = current.parent
    }
    return true
  }
}
